// Reactions Bar - Display and manage reactions on posts (one reaction per user)
import { db, auth } from '../firebase.js';
import { doc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';

export class ReactionsBar {
  constructor(postId) {
    this.postId = postId;
    this.reactions = {
      heart: { emoji: '❤️', label: 'J\'aime', color: '#ef4444' },
      amen: { emoji: '🙏', label: 'Amen', color: '#3b82f6' },
      thoughtful: { emoji: '🤔', label: 'Réfléchi', color: '#f59e0b' },
      encouraging: { emoji: '😊', label: 'Encourageant', color: '#10b981' },
      inspiring: { emoji: '🔥', label: 'Inspirant', color: '#8b5cf6' }
    };
  }

  async render(container, postData) {
    const currentUser = auth.currentUser;
    const reactions = postData.reactions || {};
    
    // Find user's current reaction (only one allowed)
    let userReaction = null;
    if (currentUser) {
      for (const [type, users] of Object.entries(reactions)) {
        if (users?.includes(currentUser.uid)) {
          userReaction = type;
          break;
        }
      }
    }
    
    // Calculate total reactions count
    const totalReactions = Object.values(reactions).reduce((sum, arr) => sum + (arr?.length || 0), 0);
    
    // Create main reaction button
    const mainBtnEmoji = userReaction ? this.reactions[userReaction].emoji : '👍';
    const mainBtnLabel = userReaction ? this.reactions[userReaction].label : 'Réagir';
    
    const reactionsContainer = document.createElement('div');
    reactionsContainer.className = 'reactions-bar-container';
    reactionsContainer.id = `reactions-${this.postId}`;
    
    reactionsContainer.innerHTML = `
      <button class="main-reaction-btn ${userReaction ? 'reacted' : ''}">
        <span class="main-reaction-emoji">${mainBtnEmoji}</span>
        <span class="main-reaction-label">${mainBtnLabel}</span>
        ${totalReactions > 0 ? `<span class="total-reactions-count">${totalReactions}</span>` : ''}
      </button>
      
      <div class="reactions-menu" style="display: none;">
        ${Object.entries(this.reactions).map(([type, data]) => {
          const count = reactions[type]?.length || 0;
          const isActive = type === userReaction;
          return `
            <button class="reaction-option ${isActive ? 'active' : ''}" data-type="${type}">
              <span class="reaction-option-emoji">${data.emoji}</span>
              <span class="reaction-option-label">${data.label}</span>
              ${count > 0 ? `<span class="reaction-option-count">${count}</span>` : ''}
            </button>
          `;
        }).join('')}
      </div>
    `;
    
    container.appendChild(reactionsContainer);
    
    // Attach event listeners
    const mainBtn = reactionsContainer.querySelector('.main-reaction-btn');
    const menu = reactionsContainer.querySelector('.reactions-menu');
    const options = reactionsContainer.querySelectorAll('.reaction-option');
    
    mainBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.toggleMenu(menu);
    });
    
    options.forEach(opt => {
      opt.addEventListener('click', async (e) => {
        e.stopPropagation();
        await this.selectReaction(opt.dataset.type, userReaction);
        this.closeMenu(menu);
      });
    });
    
    // Close menu when clicking outside
    document.addEventListener('click', (e) => {
      if (!reactionsContainer.contains(e.target)) {
        this.closeMenu(menu);
      }
    });
  }

  toggleMenu(menu) {
    if (menu.style.display === 'none') {
      menu.style.display = 'block';
    } else {
      this.closeMenu(menu);
    }
  }

  closeMenu(menu) {
    menu.style.display = 'none';
  }

  async selectReaction(newType, currentType) {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      alert('Vous devez être connecté pour réagir');
      return;
    }

    try {
      const postRef = doc(db, 'posts', this.postId);
      
      // If clicking on the same reaction, remove it
      if (newType === currentType) {
        await updateDoc(postRef, {
          [`reactions.${currentType}`]: arrayRemove(currentUser.uid)
        });
        console.log(`✅ Réaction ${currentType} retirée`);
      } else {
        // Prepare updates: remove from current (if any) and add to new
        const updates = {};
        
        if (currentType) {
          updates[`reactions.${currentType}`] = arrayRemove(currentUser.uid);
        }
        
        // First remove old reaction if exists
        if (currentType) {
          await updateDoc(postRef, {
            [`reactions.${currentType}`]: arrayRemove(currentUser.uid)
          });
        }
        
        // Then add new reaction
        await updateDoc(postRef, {
          [`reactions.${newType}`]: arrayUnion(currentUser.uid)
        });
        
        console.log(`✅ Réaction changée vers ${newType}`);
      }
    } catch (error) {
      console.error('Erreur lors de la réaction:', error);
      alert('Impossible de modifier la réaction');
    }
  }

  hasUserReacted(reactionType, reactionUsers) {
    if (!auth.currentUser) return false;
    return reactionUsers.includes(auth.currentUser.uid);
  }

  countReactionUsers(reactionUsers) {
    return Array.isArray(reactionUsers) ? reactionUsers.length : 0;
  }
}
